/**
 * Kararlar presenter'i
 */

import { AppState } from '../../modules/state.js';
import { FirebaseService } from '../../shared/services/firebase.service.js';
import { COLLECTIONS } from '../../firebase-config.js';
import { formatDate } from '../../shared/utils/formatters.js';

interface DecisionsPresenterDeps {
    openModal: (modalId: string) => void;
    showToast: (message: string, type?: string) => void;
    generateId: () => string;
    escapeHtml: (value: string) => string;
}

function getSortedDecisions() {
    return [...AppState.decisions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function getDecisionTagPresenter(title: string, content: string): string {
    const text = `${title} ${content}`.toLowerCase();
    if (text.includes('bakım') || text.includes('onarım') || text.includes('asansör')) return 'Bakım';
    if (text.includes('aidat') || text.includes('ödeme') || text.includes('bütçe')) return 'Finans';
    if (text.includes('güvenlik') || text.includes('kamera') || text.includes('kapı')) return 'Güvenlik';
    if (text.includes('toplantı') || text.includes('oy') || text.includes('karar')) return 'Yönetim';
    return 'Genel';
}

export function renderDecisionsPresenter(deps: DecisionsPresenterDeps): void {
    const container = document.getElementById('decisions-list');
    if (!container) return;

    if (AppState.decisions.length === 0) {
        container.innerHTML = '<p class="empty-state">Henüz karar eklenmemiş</p>';
        return;
    }

    const sorted = getSortedDecisions();
    const featured = sorted[0];
    const rest = sorted.slice(1);

    container.innerHTML = `
        <div class="featured-decision-card">
            <div class="featured-decision-header">
                <span class="badge primary">Öne Çıkan Karar</span>
                <span class="transaction-date">📅 ${formatDate(featured.date)}</span>
            </div>
            <h4>${deps.escapeHtml(featured.title)}</h4>
            <p>${deps.escapeHtml(featured.content).substring(0, 220)}${featured.content.length > 220 ? '...' : ''}</p>
            <div class="transaction-actions">
                <button class="btn btn-secondary btn-sm" onclick="editDecision('${featured.id}')" title="Düzenle">✏️</button>
                <button class="btn btn-danger btn-sm" onclick="deleteDecision('${featured.id}')" title="Sil">🗑️</button>
            </div>
        </div>
        ${rest.map(decision => `
        <div class="transaction-card income">
            <div class="transaction-icon">📋</div>
            <div class="transaction-info">
                <div class="transaction-category">
                    ${deps.escapeHtml(decision.title)}
                    <span class="decision-tag">${getDecisionTagPresenter(decision.title, decision.content)}</span>
                </div>
                <div class="transaction-description">${deps.escapeHtml(decision.content).substring(0, 100)}${decision.content.length > 100 ? '...' : ''}</div>
            </div>
            <div class="transaction-meta">
                <div class="transaction-date">📅 ${formatDate(decision.date)}</div>
            </div>
            <div class="transaction-actions">
                <button class="btn btn-secondary btn-sm" onclick="editDecision('${decision.id}')" title="Düzenle">✏️</button>
                <button class="btn btn-danger btn-sm" onclick="deleteDecision('${decision.id}')" title="Sil">🗑️</button>
            </div>
        </div>
        `).join('')}
    `;
}

export function renderRecentDecisionsPresenter(deps: DecisionsPresenterDeps): void {
    const container = document.getElementById('recent-decisions');
    if (!container) return;

    const recent = getSortedDecisions().slice(0, 3);
    if (recent.length === 0) {
        container.innerHTML = '<p class="empty-state">Henüz karar eklenmemiş</p>';
        return;
    }

    container.innerHTML = recent
        .map(decision => `<div class="recent-item"><h4>📋 ${deps.escapeHtml(decision.title)}</h4><p>${formatDate(decision.date)}</p></div>`)
        .join('');
}

export function renderResidentDecisionsPresenter(deps: DecisionsPresenterDeps): void {
    const container = document.getElementById('resident-decisions-list');
    if (!container) return;

    if (AppState.decisions.length === 0) {
        container.innerHTML = '<p class="empty-state">Henüz karar eklenmemiş</p>';
        return;
    }

    container.innerHTML = getSortedDecisions().map(decision => `
        <div class="decision-card glass-card">
            <div class="decision-header">
                <h3 class="decision-title">${deps.escapeHtml(decision.title)}</h3>
                <span class="decision-date">📅 ${formatDate(decision.date)}</span>
            </div>
            ${((Date.now() - new Date(decision.date).getTime()) / 86400000) <= 14 ? '<span class="resident-item-badge">Yeni</span>' : ''}
            <p class="decision-content">${deps.escapeHtml(decision.content)}</p>
        </div>
    `).join('');
}

export function renderResidentRecentDecisionsPresenter(deps: DecisionsPresenterDeps): void {
    const container = document.getElementById('resident-recent-decisions');
    if (!container) return;

    const recent = getSortedDecisions().slice(0, 5);
    if (recent.length === 0) {
        container.innerHTML = '<p class="empty-state">Henüz karar eklenmemiş</p>';
        return;
    }

    container.innerHTML = recent.map(decision => `
        <div class="compact-item clickable" onclick="viewDecisionDetail('${decision.id}')">
            <h4>📝 ${deps.escapeHtml(decision.title)} ${((Date.now() - new Date(decision.date).getTime()) / 86400000) <= 14 ? '<span class="resident-item-badge">Yeni</span>' : ''}</h4>
            <p>${formatDate(decision.date)}</p>
        </div>
    `).join('');
}

export function addDecisionPresenter(data: Record<string, any>, deps: DecisionsPresenterDeps) {
    const decision = { id: deps.generateId(), ...data, createdAt: new Date().toISOString() };
    AppState.decisions.push(decision);
    FirebaseService.add(COLLECTIONS.DECISIONS, decision);
    deps.showToast('Karar eklendi', 'success');
    return decision;
}

export function updateDecisionPresenter(id: string, data: Record<string, any>, deps: DecisionsPresenterDeps): boolean {
    const index = AppState.decisions.findIndex(decision => decision.id === id);
    if (index === -1) return false;

    const updated = { ...AppState.decisions[index], ...data };
    AppState.decisions[index] = updated;
    FirebaseService.save(COLLECTIONS.DECISIONS, id, updated);
    deps.showToast('Karar güncellendi', 'success');
    return true;
}

export function deleteDecisionPresenter(id: string, deps: DecisionsPresenterDeps): void {
    if (!confirm('Bu kararı silmek istediğinizden emin misiniz?')) return;

    AppState.decisions = AppState.decisions.filter(decision => decision.id !== id);
    FirebaseService.delete(COLLECTIONS.DECISIONS, id);
    renderDecisionsPresenter(deps);
    renderRecentDecisionsPresenter(deps);
    renderResidentDecisionsPresenter(deps);
    renderResidentRecentDecisionsPresenter(deps);
    deps.showToast('Karar silindi', 'success');
}

export function editDecisionPresenter(id: string, deps: DecisionsPresenterDeps): void {
    const decision = AppState.decisions.find(item => item.id === id);
    if (!decision) return;

    document.getElementById('decision-modal-title')!.textContent = 'Karar Düzenle';
    (document.getElementById('decision-id') as HTMLInputElement).value = decision.id;
    (document.getElementById('decision-title') as HTMLInputElement).value = decision.title;
    (document.getElementById('decision-date') as HTMLInputElement).value = decision.date;
    (document.getElementById('decision-content') as HTMLTextAreaElement).value = decision.content;
    deps.openModal('decision-modal');
}

export function viewDecisionDetailPresenter(id: string, deps: DecisionsPresenterDeps): void {
    const decision = AppState.decisions.find(item => item.id === id);
    if (!decision) return;

    document.getElementById('decision-detail-title')!.textContent = decision.title;
    document.getElementById('decision-detail-date')!.textContent = `📅 ${formatDate(decision.date)}`;
    document.getElementById('decision-detail-content')!.textContent = decision.content || 'İçerik bulunmuyor.';
    deps.openModal('decision-detail-modal');
}
